import Flag from "@common/controls/Flag/component";
import { type OptionItem } from "@common/declarations";
import { reduce, startsWith } from "lodash";
import * as React from "react";
import rawCountries from "./rawCountries";
import { type Country } from "./types";

export const trimNumber = (number: string | null): string => (number ?? "").replace(/^\+/, "");

export const formatNumber = (text: string, country: Country | null) => {
  if (!country || !text) return text;

  const { format } = country;

  let pattern = format;
  let number = trimNumber(text).replace(/[\s\(\)-]/g, "");
  if (!number || number.length === 0) {
    return "+";
  }

  // for all strings with length less than 3, just return it (1, 2 etc.)
  // also return the same text if the selected country has no fixed format
  if ((number && number.length < 2) || !pattern) {
    return "+" + number;
  }

  const formattedObject = reduce(
    pattern,
    (acc, character) => {
      if (acc.remainingText.length === 0) {
        return acc;
      }

      if (character !== ".") {
        return {
          formattedText: acc.formattedText + character,
          remainingText: acc.remainingText
        };
      }

      const [head, ...tail] = acc.remainingText;

      return {
        formattedText: acc.formattedText + head,
        remainingText: tail
      };
    },
    {
      formattedText: "",
      remainingText: number.split("")
    }
  );

  let formattedNumber = formattedObject.formattedText;

  // Always close brackets
  if (formattedNumber.includes("(") && !formattedNumber.includes(")")) formattedNumber += ")";
  return formattedNumber ?? "";
};

const getMask = (prefix, dialCode, predefinedMask, defaultMask, alwaysDefaultMask): string => {
  if (!predefinedMask || alwaysDefaultMask) {
    return prefix + "".padEnd(dialCode.length, ".") + " " + defaultMask;
  } else {
    return prefix + "".padEnd(dialCode.length, ".") + " " + predefinedMask;
  }
};

export const formatPhoneNumber = (phone: string, countryCode: string | null): string =>
  formatNumber(phone, countryCode ? COUNTRIES.find((x) => x.iso2.toLowerCase() === countryCode.toLowerCase())! : null);

export const padEnd = (target: string, targetLength: number, padString: string) => {
  targetLength = targetLength >> 0; //floor if number or convert non-number to 0;
  padString = String(typeof padString !== "undefined" ? padString : " ");
  if (target.length > targetLength) {
    return String(this);
  } else {
    targetLength = targetLength - target.length;
    if (targetLength > padString.length) {
      padString += padString.repeat(targetLength / padString.length); //append to original to ensure we are longer than needed
    }
    return String(this) + padString.slice(0, targetLength);
  }
};

export const COUNTRIES = ((
  countries: any,
  prefix: string,
  defaultMask: string,
  alwaysDefaultMask: boolean
): Country[] => {
  const initializedCountries: Omit<Country, "id">[] = [].concat(
    ...countries.map((country, index) => {
      const countryItem: Omit<Country, "id"> = {
        name: country[0],
        regions: country[1],
        iso2: country[2],
        countryCode: country[3],
        dialCode: country[3],
        format: getMask(prefix, country[3], country[4], defaultMask, alwaysDefaultMask),
        priority: country[5] || 0,
        mainCode: false,
        hasAreaCodes: false
      };

      const areaItems = country[6]
        ? country[6].map((areaCode) => ({
            ...countryItem,
            dialCode: country[3] + areaCode,
            isAreaCode: true,
            areaCodeLength: areaCode.length
          }))
        : [];

      if (areaItems.length > 0) {
        countryItem.mainCode = true;
        countryItem.hasAreaCodes = true;
        return [countryItem, ...areaItems];
      } else {
        return [countryItem];
      }
    })
  );

  return initializedCountries.map((x, i) => ({ ...x, id: i.toString() }));
})(rawCountries, "+", "... ... ... ... ..", false);

export const getDialCode = (countryCode: string | null): string | null => {
  if (!countryCode) return null;

  const cc = countryCode.toLowerCase();
  const country = COUNTRIES.find((x) => x.iso2 === cc);
  return country ? `+${country.dialCode}` : null;
};

export const guessCountry = (text: string): Country | null => {
  // if enableAreaCodes == false, try to search in hidden area codes to detect area code correctly
  // then search and insert main country which has this area code
  // https://github.com/bl00mber/react-phone-input-2/issues/201

  const inputNumber = trimNumber(text);
  const bestGuess = COUNTRIES.reduce(
    (selectedCountry, x) => {
      if (startsWith(inputNumber, x.dialCode)) {
        if (x.dialCode.length > selectedCountry.dialCode.length) {
          return x;
        }
        if (x.dialCode.length === selectedCountry.dialCode.length && x.priority < selectedCountry.priority) {
          return x;
        }
      }
      return selectedCountry;
    },
    { dialCode: "", priority: 10001 } as Country
  );

  return !bestGuess.dialCode ? null : bestGuess;
};

export const COUNTRY_OPTIONS: OptionItem<string>[] = COUNTRIES.map<OptionItem<string>>((c) => ({
  prepend: <Flag iso2={c.iso2} size={16} isRound style={{ marginRight: 6 }} />,
  label: `${c.name} +${c.dialCode}`,
  value: c.id
}));
